import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  FaArrowRight, FaStar, FaShippingFast, FaShieldAlt,
  FaTags, FaChevronRight, FaChevronLeft, FaClock,
  FaUmbrellaBeach, FaSun,
  FaShoppingBag, FaCrown, FaPercent, FaLeaf
} from 'react-icons/fa';
import {
  collection, getDocs, query, limit, orderBy, where, documentId, doc, getDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';
import ProductCard from '../components/ProductCard';
import { buildLiveCategories, isVisibleProduct } from '../utils/categoryImage';
import { cdnImage } from '../utils/img';
import { useSiteContent, kshAmount } from '../hooks/useSiteContent';
import StyleLookModal from '../components/StyleLookModal';
import Seo from '../components/Seo';
import './Home.css';

// Shown on the hero the instant the page renders, so the LCP image no longer
// waits for the client-side Firestore `settings/hero` read (that read added
// ~2.5s of "resource load delay" to LCP). Swapped for the admin-chosen hero as
// soon as fetchHeroImage() resolves. This exact URL (via /img at w=600) is
// <link rel="preload">-ed in public/index.html so the browser starts fetching
// it before the JS bundle even executes.
const DEFAULT_HERO = 'https://images.unsplash.com/photo-1612423284934-2850a4ea6b0f?w=800&auto=format&fit=crop';
// Hero render width. Kept modest (was 800) — the framed hero never displays
// wider than ~460px, so 600 covers hi-dpi mobile while trimming bytes. Must
// stay in sync with the preload width in public/index.html.
const HERO_W = 600;

function useTilt() {
  const ref = useRef(null);
  const onMouseMove = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    const { left, top, width, height } = el.getBoundingClientRect();
    const x = (e.clientX - left) / width - 0.5;
    const y = (e.clientY - top) / height - 0.5;
    el.style.transform = `perspective(800px) rotateX(${-y * 10}deg) rotateY(${x * 10}deg) scale3d(1.04,1.04,1.04)`;
  }, []);
  const onMouseLeave = useCallback(() => {
    if (ref.current) {
      ref.current.style.transform =
        'perspective(800px) rotateX(0deg) rotateY(0deg) scale3d(1,1,1)';
    }
  }, []);
  return { ref, onMouseMove, onMouseLeave };
}

// Matches the card style used on the Categories page (rounded, soft shadow,
// lift on hover, a description line and pill "Shop Now").
function CategoryCard({ category }) {
  const hasImage = !!category.image;
  return (
    <Link
      to={`/shop?category=${encodeURIComponent(category.name.toLowerCase().replace(/\s+/g, '-'))}`}
      className="category-card-img"
    >
      {hasImage ? (
        <div className="cat-img-bg" style={{ backgroundImage: `url(${cdnImage(category.image, 400)})` }} />
      ) : (
        <div
          className="cat-img-bg"
          style={{ background: 'linear-gradient(135deg, #FF9A9E 0%, #FAD0C4 100%)' }}
        />
      )}
      <div className="cat-img-overlay" />
      <div className="cat-img-body">
        <h4 className="cat-img-name">{category.name}</h4>
        <p className="cat-img-desc">{category.description || `Shop ${category.name}`}</p>
        {category.count && <span className="cat-img-count">{category.count}</span>}
        <span className="cat-img-cta">Shop Now <FaArrowRight /></span>
      </div>
    </Link>
  );
}

function useCountdown(targetDate) {
  const calc = () => {
    const diff = Math.max(0, targetDate - Date.now());
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff % 86400000) / 3600000),
      mins: Math.floor((diff % 3600000) / 60000),
      secs: Math.floor((diff % 60000) / 1000),
    };
  };
  const [time, setTime] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(id);
  }, [targetDate]);
  return time;
}

const Home = () => {
  const [newArrivals, setNewArrivals] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [styleLooks, setStyleLooks] = useState([]);
  const [activeLook, setActiveLook] = useState(null);
  const { content } = useSiteContent();
  const testimonials = content.testimonials;
  const [loading, setLoading] = useState(true);
  const [activeHero, setActiveHero] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [heroProductImage, setHeroProductImage] = useState(DEFAULT_HERO);

  const saleEnd = useRef(Date.now() + 3 * 86400000 + 12 * 3600000 + 45 * 60000).current;
  const countdown = useCountdown(saleEnd);
  const pad = (n) => String(n).padStart(2, '0');

  const heroSlides = [
    {
      title: 'Summer Glow',
      titleAccent: 'Collection',
      subtitle: 'Discover premium beachwear crafted for confidence and style. Exclusive designs just for you.',
      // Seeded with DEFAULT_HERO for instant first paint, then swapped to the
      // admin-chosen product image once fetchHeroImage() resolves.
      image: heroProductImage,
      gradient: 'linear-gradient(135deg, #ff9a9e 0%, #fad0c4 60%, #ffecd2 100%)',
      badge: 'Up to 40% Off',
      cta: 'Shop Now',
      accent: '#FF6B9D',
    },
    {
      title: 'New Beach',
      titleAccent: 'Arrivals',
      subtitle: 'Fresh tropical styles just dropped. Be the first to wear the latest trends this season.',
      image: 'https://images.unsplash.com/photo-1582639590011-f5a8416d1101?w=800&auto=format&fit=crop',
      gradient: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 60%, #d4fc79 100%)',
      badge: 'Limited Edition',
      cta: 'View Collection',
      accent: '#4ECDC4',
    },
    {
      title: 'Designer',
      titleAccent: 'Swimwear',
      subtitle: 'Luxury beachwear for the modern fashionista. Elevate every beach moment.',
      image: 'https://images.unsplash.com/photo-1612423284934-2850a4ea6b0f?w=800&auto=format&fit=crop',
      gradient: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 60%, #ff9a9e 100%)',
      badge: 'Free Shipping',
      cta: 'Explore Luxury',
      accent: '#FFA726',
    },
  ];

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    if (!isPlaying) return;
    const id = setInterval(() => setActiveHero((p) => (p + 1) % heroSlides.length), 5000);
    return () => clearInterval(id);
  }, [isPlaying, heroSlides.length]);

  const goTo = (i) => {
    setActiveHero(i);
    setIsPlaying(false);
    setTimeout(() => setIsPlaying(true), 10000);
  };

  // Every category with at least one published product gets a card, showing a
  // photo of a random product from it. The `categories` docs only add
  // metadata (description, order); they don't decide what appears.
  const displayCategories = useMemo(
    () => buildLiveCategories(allProducts, categories)
      .map((c) => ({ ...c, count: `${c.count} ${c.count === 1 ? 'style' : 'styles'}` })),
    [categories, allProducts]
  );

  const fetchAll = async () => {
    try {
      await Promise.all([fetchProducts(), fetchCategories(), fetchBestSellers(), fetchHeroImage(), fetchStyleLooks()]);
    } finally {
      setLoading(false);
    }
  };

  const fetchHeroImage = async () => {
    try {
      // 1. Admin-chosen default hero image. Stored in `settings/hero` because that
      //    collection is publicly readable — guests can load it reliably.
      const heroSettingSnap = await getDoc(doc(db, 'settings', 'hero'));
      if (heroSettingSnap.exists()) {
        const url = heroSettingSnap.data().imageUrl;
        if (url) { setHeroProductImage(url); return; }
      }

      // 2. Fall back (admin/signed-in only): newest uploaded library image.
      const libSnap = await getDocs(
        query(collection(db, 'productImages'), orderBy('uploadedAt', 'desc'), limit(1))
      );
      if (!libSnap.empty) {
        const url = libSnap.docs[0].data().url;
        if (url) { setHeroProductImage(url); return; }
      }

      // 3. Fall back: scan published products for any Firebase Storage image
      const prodSnap = await getDocs(
        query(collection(db, 'products'), where('webActive', '==', true), limit(30))
      );
      for (const d of prodSnap.docs) {
        const imgs = d.data().images || [];
        const storageUrl = imgs.find(u => u && u.includes('firebasestorage.googleapis.com'));
        if (storageUrl) { setHeroProductImage(storageUrl); return; }
      }
    } catch (e) {
      console.error('Hero image fetch failed:', e);
    }
  };

  const fetchStyleLooks = async () => {
    try {
      const snap = await getDocs(
        query(collection(db, 'styleLooks'), orderBy('createdAt', 'desc'), limit(12))
      );
      setStyleLooks(
        snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(l => l.active !== false)
      );
    } catch (e) {
      console.error('Style looks fetch failed:', e);
    }
  };

  const fetchProducts = async () => {
    try {
      // Fetch one pool without orderBy (orderBy would drop products missing
      // `featured`/`createdAt`, e.g. POS-created items) and derive both rows.
      // Filter to published stock in the *query*, not client-side: the
      // collection is shared with the POS, so an unfiltered limit(200) could
      // return 200 in-store-only docs and leave the homepage empty.
      const snap = await getDocs(
        query(collection(db, 'products'), where('webActive', '==', true), limit(200))
      );
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter(isVisibleProduct);
      setAllProducts(all);

      const toMillis = (v) =>
        v?.toMillis ? v.toMillis() : (v?.seconds ? v.seconds * 1000 : (v ? new Date(v).getTime() || 0 : 0));

      const newest = [...all].sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
      setNewArrivals(newest.slice(0, 4));
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const fetchCategories = async () => {
    try {
      // Fetch whole: where('active')/orderBy('order') would silently drop docs
      // missing those fields. Inactive docs are skipped in buildLiveCategories.
      const snap = await getDocs(collection(db, 'categories'));
      setCategories(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchBestSellers = async () => {
    try {
      const shopsSnap = await getDocs(
        query(collection(db, 'shops'), where('isActive', '==', true))
      );
      const shops = shopsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      if (shops.length === 0) {
        const fallback = await getDocs(
          query(collection(db, 'products'), where('webActive', '==', true), limit(60))
        );
        setBestSellers(
          fallback.docs.map(d => ({ id: d.id, ...d.data() })).filter(isVisibleProduct).slice(0, 4)
        );
        return;
      }

      const salesCounterMap = {};

      await Promise.all(
        shops.map(async (shop) => {
          try {
            const salesSnap = await getDocs(
              query(
                collection(db, 'shops', shop.id, 'sales'),
                orderBy('createdAt', 'desc'),
                limit(100)
              )
            );
            salesSnap.docs.forEach(saleDoc => {
              const sale = saleDoc.data();
              const items = (sale.items || []);
              items.forEach(item => {
                const pid = item.productId?.toString();
                if (!pid) return;
                const qty = (item.quantity ?? item.cartQuantity ?? 0);
                const rev = (item.subtotal ?? 0);
                if (!salesCounterMap[pid]) {
                  salesCounterMap[pid] = { totalQty: 0, totalRevenue: 0, name: item.name || item.productName || '' };
                }
                salesCounterMap[pid].totalQty += qty;
                salesCounterMap[pid].totalRevenue += rev;
              });
            });
          } catch (_) {}
        })
      );

      const sorted = Object.entries(salesCounterMap)
        .sort(([, a], [, b]) => b.totalQty - a.totalQty)
        .slice(0, 4)
        .map(([id]) => id);

      if (sorted.length === 0) {
        const fallback = await getDocs(
          query(collection(db, 'products'), where('webActive', '==', true), limit(60))
        );
        setBestSellers(
          fallback.docs.map(d => ({ id: d.id, ...d.data() })).filter(isVisibleProduct).slice(0, 4)
        );
        return;
      }

      const productsSnap = await getDocs(
        query(collection(db, 'products'), where(documentId(), 'in', sorted))
      );
      const productsById = {};
      productsSnap.docs.forEach(d => { productsById[d.id] = { id: d.id, ...d.data() }; });
      // Top sellers may include in-store-only stock — keep only what's published.
      const ordered = sorted.map(id => productsById[id]).filter(isVisibleProduct);
      setBestSellers(ordered);
    } catch (err) {
      console.error('Error fetching best sellers:', err);
    }
  };

  const features = [
    { icon: <FaShippingFast />, title: 'Free Shipping', desc: `On orders over ${kshAmount(content.shipping?.freeThreshold)} with 3-5 day delivery`, gradient: 'linear-gradient(135deg,#FF9A9E,#FAD0C4)', bg: '#FFF0F3' },
    { icon: <FaShieldAlt />, title: 'Quality Guarantee', desc: 'Premium materials and perfect fit guaranteed', gradient: 'linear-gradient(135deg,#A1C4FD,#C2E9FB)', bg: '#F0F8FF' },
    { icon: <FaTags />, title: 'Best Price', desc: 'Price match guarantee on all swimwear', gradient: 'linear-gradient(135deg,#FFECD2,#FCB69F)', bg: '#FFF8F0' },
    { icon: <FaLeaf />, title: 'Eco Friendly', desc: 'Sustainably sourced fabrics & packaging', gradient: 'linear-gradient(135deg,#D4FC79,#96E6A1)', bg: '#F0FFF4' },
  ];

  return (
    <div className="home-page bikini-world">
      <Seo path="/" />

      {/* ════════ HERO ════════ */}
      <section className="hero-v2">
        <div className="hero-blobs">
          <div className="blob blob-1" />
          <div className="blob blob-2" />
          <div className="blob blob-3" />
        </div>

        {heroSlides.map((slide, i) => (
          <div key={i} className={`hero-v2-slide ${i === activeHero ? 'active' : ''}`}>
            <div className="container">
              <div className="hero-v2-inner">
                <div className="hero-v2-text">
                  <span className="hero-v2-badge" style={{ '--badge-color': slide.accent }}>
                    {slide.badge}
                  </span>
                  <h1 className="hero-v2-title">
                    {slide.title}<br />
                    <span className="hero-v2-accent" style={{ '--accent': slide.accent }}>
                      {slide.titleAccent}
                    </span>
                  </h1>
                  <p className="hero-v2-subtitle">{slide.subtitle}</p>
                  <Link to="/shop" className="hero-v2-btn" style={{ '--btn-color': slide.accent }}>
                    <span>{slide.cta}</span>
                    <span className="hero-v2-btn-arrow"><FaArrowRight /></span>
                  </Link>
                  <div className="hero-v2-pills">
                    {heroSlides.map((_, j) => (
                      <button
                        key={j}
                        className={`hero-v2-pill ${j === activeHero ? 'active' : ''}`}
                        style={{ '--pill-color': heroSlides[activeHero].accent }}
                        onClick={() => goTo(j)}
                        aria-label={`Slide ${j + 1}`}
                      />
                    ))}
                  </div>
                </div>
                <div className="hero-v2-image-wrap">
                  <div className="hero-v2-image-frame">
                    {slide.image ? (
                      // Hero is the LCP element — load it eagerly at high
                      // priority the moment its URL resolves (only the active
                      // slide, so we don't fight it with the off-screen ones).
                      <img
                        src={cdnImage(slide.image, HERO_W)}
                        alt={slide.title}
                        className="hero-v2-img"
                        // Only the first/active slide is the LCP — fetch it
                        // eagerly at high priority; defer the off-screen slides
                        // so they don't steal bandwidth from it on first load.
                        fetchPriority={i === activeHero ? 'high' : 'low'}
                        loading={i === activeHero ? 'eager' : 'lazy'}
                        decoding="async"
                      />
                    ) : (
                      <div
                        className="hero-v2-img hero-v2-img-placeholder"
                        style={{ background: slide.gradient }}
                        aria-label={slide.title}
                      />
                    )}
                    <div className="hero-v2-image-glow" style={{ '--glow': slide.accent }} />
                  </div>
                  <div className="hero-v2-float-tag" style={{ '--tag-color': slide.accent }}>
                    <FaStar style={{ color: '#FFD700' }} />
                    <span>4.9 / 5 Rated</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        <button className="hero-v2-nav prev" onClick={() => goTo((activeHero - 1 + heroSlides.length) % heroSlides.length)}>
          <FaChevronLeft />
        </button>
        <button className="hero-v2-nav next" onClick={() => goTo((activeHero + 1) % heroSlides.length)}>
          <FaChevronRight />
        </button>

        <div className="hero-scroll-hint">
          <div className="scroll-mouse">
            <div className="scroll-dot" />
          </div>
        </div>
      </section>

      {/* ════════ MARQUEE TRUST BAR ════════ */}
      <div className="trust-bar">
        <div className="trust-track">
          {[
            `Free Shipping Over ${kshAmount(content.shipping?.freeThreshold)}`, 'Secure Checkout', '15,000+ Happy Customers',
            'Eco-Friendly Fabrics', 'Easy Returns', 'Award-Winning Designs',
            `Free Shipping Over ${kshAmount(content.shipping?.freeThreshold)}`, 'Secure Checkout', '15,000+ Happy Customers',
            'Eco-Friendly Fabrics', 'Easy Returns', 'Award-Winning Designs',
          ].map((item, i) => (
            <span key={i} className="trust-item">{item}</span>
          ))}
        </div>
      </div>

      {/* ════════ CATEGORIES ════════ */}
      {displayCategories.length > 0 && (
        <section className="categories-section">
          <div className="container">
            <div className="section-header-center">
              <p className="section-eyebrow">Browse Our Range</p>
              <h2>Shop by Category</h2>
              <p className="section-sub">Find your perfect beach style</p>
            </div>
            <div className="categories-grid">
              {displayCategories.map((cat) => (
                <CategoryCard key={cat.id} category={cat} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ════════ BEST SELLERS ════════ */}
      {bestSellers.length > 0 && (
        <section className="best-sellers-section">
          <div className="container">
            <div className="section-header">
              <div>
                <p className="section-eyebrow">Customer Favourites</p>
                <h2>Best Sellers</h2>
              </div>
              <Link to="/shop?sort=best-selling" className="view-all">View All <FaArrowRight /></Link>
            </div>
            <div className="products-grid">
              {bestSellers.map((p) => <ProductCard key={p.id} product={p} compact />)}
            </div>
          </div>
        </section>
      )}

      {/* ════════ PROMO BANNER ════════ */}
      <section className="promo-banner-v2">
        <div className="container">
          <div className="promo-inner">
            <div className="promo-text">
              <span className="promo-badge"><FaPercent /> Limited Time Offer</span>
              <h2>Summer Glow Sale<br /><span className="promo-highlight">Up to 50% Off</span></h2>
              <p>Don't miss out on incredible deals across all swimwear collections.</p>
              <Link to="/shop?sort=discount" className="btn-promo">
                Shop the Sale <FaArrowRight />
              </Link>
            </div>
            <div className="promo-countdown-wrap">
              <p className="countdown-label">Offer ends in</p>
              <div className="countdown-grid">
                {[
                  { val: pad(countdown.days), unit: 'Days' },
                  { val: pad(countdown.hours), unit: 'Hrs' },
                  { val: pad(countdown.mins), unit: 'Min' },
                  { val: pad(countdown.secs), unit: 'Sec' },
                ].map(({ val, unit }) => (
                  <div className="countdown-cell" key={unit}>
                    <span className="countdown-num">{val}</span>
                    <span className="countdown-unit">{unit}</span>
                  </div>
                ))}
              </div>
              <Link to="/shop?sort=discount" className="btn-promo-alt">
                View All Deals
              </Link>
            </div>
            <span className="promo-palm" aria-hidden>🌴</span>
          </div>
        </div>
      </section>

      {/* ════════ NEW ARRIVALS ════════ */}
      <section className="new-arrivals">
        <div className="container">
          <div className="section-header">
            <div>
              <p className="section-eyebrow">Just Landed</p>
              <h2>New Beach Arrivals</h2>
            </div>
            <Link to="/shop?sort=newest" className="view-all">View All <FaArrowRight /></Link>
          </div>
          <div className="products-grid">
            {newArrivals.map((p) => <ProductCard key={p.id} product={p} compact />)}
          </div>
        </div>
      </section>

      {/* ════════ PICKED FOR YOU ════════ */}
      {styleLooks.length > 0 && (
        <section className="picked-section">
          <div className="container">
            <div className="section-header-center">
              <p className="section-eyebrow">Styled by Us</p>
              <h2>Picked for You</h2>
              <p className="picked-sub">Complete looks, styled and ready to shop</p>
            </div>
            <div className="picked-grid">
              {styleLooks.map((look) => {
                const shoppable = look.products && look.products.length > 0;
                const CardInner = (
                  <>
                    <div className="picked-img">
                      <img src={look.imageUrl} alt={look.title || 'Styled look'} loading="lazy" />
                      {look.discountPercent > 0 && (
                        <span className="picked-disc">-{look.discountPercent}%</span>
                      )}
                      <div className="picked-overlay">
                        <span className="picked-shop">Shop the look <FaArrowRight /></span>
                      </div>
                    </div>
                    {look.title && <span className="picked-title">{look.title}</span>}
                    {shoppable && (
                      <span className="picked-count">{look.products.length} pieces</span>
                    )}
                  </>
                );
                return shoppable ? (
                  <button type="button" className="picked-card" key={look.id} onClick={() => setActiveLook(look)}>
                    {CardInner}
                  </button>
                ) : (
                  <Link to="/shop" className="picked-card" key={look.id}>
                    {CardInner}
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {activeLook && (
        <StyleLookModal look={activeLook} onClose={() => setActiveLook(null)} />
      )}

      {/* ════════ TESTIMONIALS ════════ */}
      <section className="testimonials">
        <div className="container">
          <div className="section-header-center">
            <p className="section-eyebrow">Customer Love</p>
            <h2>What Beach Lovers Say</h2>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((t, i) => (
              <div className="testimonial-card-v2" key={i}>
                <div className="testimonial-stars">
                  {[...Array(5)].map((_, j) => <FaStar key={j} style={{ color: '#FFD700' }} />)}
                </div>
                <p className="testimonial-quote">
                  {/^["'].*["']$/.test(t.quote?.trim() || '') ? t.quote : `"${t.quote}"`}
                </p>
                <div className="testimonial-footer">
                  <img src={t.avatar} alt={t.name} className="testimonial-avatar" />
                  <div>
                    <strong>{t.name}</strong>
                    <span>{t.role}</span>
                  </div>
                  {t.emoji && <span className="testimonial-emoji">{t.emoji}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ FEATURES ════════ */}
      <section className="features-section">
        <div className="container">
          <div className="section-header-center">
            <p className="section-eyebrow">Our Promise</p>
            <h2>Why Choose Bikini World</h2>
          </div>
          <div className="features-grid">
            {features.map((f, i) => (
              <div className="feature-card-v2" key={i} style={{ '--feat-bg': f.bg }}>
                <div className="feature-icon-v2" style={{ background: f.gradient }}>
                  {f.icon}
                </div>
                <h4>{f.title}</h4>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ NEWSLETTER ════════ */}
      <section className="newsletter-v2">
        <div className="newsletter-blobs">
          <div className="nl-blob nl-blob-1" />
          <div className="nl-blob nl-blob-2" />
        </div>
        <div className="container">
          <div className="newsletter-card">
            <div className="newsletter-icon">🌴</div>
            <h2>Get Beach Ready</h2>
            <p>Subscribe for exclusive offers, styling tips & early access to new collections.</p>
            <form className="newsletter-form" onSubmit={(e) => e.preventDefault()}>
              <div className="nl-input-wrap">
                <input type="email" placeholder="Enter your email address" required />
                <button type="submit">
                  Subscribe <FaSun style={{ marginLeft: 8 }} />
                </button>
              </div>
            </form>
            <p className="nl-note">🔒 No spam, ever. Unsubscribe anytime.</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
